<template>
  <div>
    <div ref='canvasWrap' class="canvas-wrap">
      <canvas ref='canvas'></canvas>
      <p class='viewing-file-name' v-if="status === 'view'">{{ viewingFileName }}</p>
      <b-button type='is-info' class="new-file-button" v-if="status === 'view'" @click="status = 'load'">別のファイルを開く</b-button>
      <b-message class="viewer-message" type="is-info" title="自動見積もり" :active="status === 'load'" @close="status = 'view'" >
        <div class="content">
          <p>お見積もりする3Dモデルを選択してください。体積を元に造形費用を計算します。</p>
          <ul>
            <li>.stlファイルのみ選択可能です。</li>
            <li>ただ今同時に複数のファイルの自動見積もりには対応しておりません</li>
          </ul>
        </div>
        <b-field class="file is-centered has-addons-centered">
          <b-upload v-model="newFile" @input="validateNewFile" accept='.stl'>
              <a class="button">
                  <font-awesome-icon icon="upload" />
                  <span>3Dモデルファイルを選択</span>
              </a>
          </b-upload>
          <span class="file-name" v-if="newFile">
              {{ newFile.name }}
          </span>
        </b-field>
        <div v-for='error in validateFileErrors' :key='error'>
          <p class="help is-danger">{{ error }}</p>
        </div>
        
        <div class="has-text-centered">
          <b-button class="is-centered" size="is-medium" type="is-info" :disabled="!newFilePassed" @click="startQuote" :loading="isLoading">お見積もりを開始</b-button>
        </div>
      </b-message>
      <b-message title="ブラウザが自動見積もりに対応していません" :closable="false" :active="oldBrowser">
        このブラウザは自動見積もり機能に対応していません。自動見積もり機能をご使用したい場合、最新版のChrome, Firefox, Edgeなどのブラウザからアクセスしてください。<br>
        自動見積もりをご使用しなくても、以下のフォームからお問い合わせは可能です。
      </b-message>
      <b-message title="3Dデータの読み込みエラー" type='is-warning' :active="status === 'error'" @close="status = 'view'">
        大変申し訳ございませんが、3Dデータの読み込みに失敗しました。<br>
        ファイルが壊れているか、3Dデータの内容に問題がある可能性があります。<br>
        自動見積もりをご使用しなくても、以下のフォームからお問い合わせは可能です。
      </b-message>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three'
import OrbitControls from 'orbit-controls-es6';
const STLLoader = require('three-stl-loader')(THREE);
import { mapMutations } from 'vuex'

export default {
  data() {
    return {
      renderer: null,
      scene: new THREE.Scene(),
      camera: new THREE.PerspectiveCamera(60, 1, 0.1, 1500),
      dirLight: new THREE.DirectionalLight(0xffffff, 0.5),
      ambLight: new THREE.AmbientLight(0xffffff, 0.5),
      gridHelper: new THREE.GridHelper(1400, 14, 0xccaaaa),
      axisHelper: new THREE.AxesHelper(100),
      modelGeo: new THREE.BufferGeometry(),
      modelMesh: new THREE.Mesh(),
      bBox: new THREE.Box3(),
      bBoxHelper: new THREE.BoxHelper(this.modelMesh, 0xcccccc),
      status: 'init',// init, load, view, error
      newFile: null,
      validateFileErrors: [],
      isLoading: false,
      allowedExtensions: ['stl'],
      viewingFileName: '',
      viewingFileVolume: 0,
    }
  },
  computed: {
    oldBrowser() {
      // Check for the various File API support.
      return !(window.File && window.FileReader && window.FileList && window.Blob) 
    },
    newFilePassed() {
      if (this.newFile == null) { return false }
      return (this.validateFileErrors.length === 0) 
    },
    bBoxWidth() {
      const bSize = new THREE.Vector3()
      this.bBox.getSize(bSize)
      return Math.floor(bSize.x * 100) / 100
    },
    bBoxHeight() {
      const bSize = new THREE.Vector3()
      this.bBox.getSize(bSize)
      return Math.floor(bSize.y * 100) / 100
    },
    bBoxDepth() {
      const bSize = new THREE.Vector3()
      this.bBox.getSize(bSize)
      return Math.floor(bSize.z * 100) / 100
    }
  },
  watch: {
    modelMesh: function(newMesh, oldMesh) {
      this.bBoxHelper.setFromObject(newMesh)
      this.bBoxHelper.update()
    }
  },
  mounted() {
    const canvas = this.$refs.canvas;
    
    const width = this.$refs.canvasWrap.clientWidth;
    const height = this.$refs.canvasWrap.clientHeight;
    
    // setup renderer
    this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        canvas: canvas
      })
    // this.renderer.setPixelRatio(window.devicePixelRatio)
    // this.renderer.setSize(width, height)
    
    // setup scene
    this.scene.background = new THREE.Color(0xf8f8f8)

    // setup camera
    // this.camera.aspect = width / height
    this.camera.position.set(0, 200, 300);
    this.camera.lookAt(new THREE.Vector3(0, 0, 0));

    // this.camera.updateProjectionMatrix();
    this.resize()
    window.addEventListener('resize', this.resize)
    // setup lights
    this.dirLight.position.set(1, 1, 1);
    this.scene.add(this.dirLight)
    this.scene.add(this.ambLight)
    
    // setup helpers
    this.scene.add(this.gridHelper)
    this.scene.add(this.axisHelper)
    this.scene.add(this.bBoxHelper)

    const controls = new OrbitControls(this.camera, this.renderer.domElement);
    
    this.animate()

    if (this.oldBrowser) {
      this.status = 'error'
      return
    }
    this.status = 'load'
  },
  methods: {
    resize() {
      const canvas = this.$refs.canvas;

      const width = this.$refs.canvasWrap.clientWidth;
      const height = this.$refs.canvasWrap.clientHeight;

      this.renderer.setPixelRatio(window.devicePixelRatio)
      this.renderer.setSize(width, height)
      
      // setup camera
      this.camera.aspect = width / height
    this.camera.updateProjectionMatrix();

    },
    animate() {
      this.renderer.render(this.scene, this.camera)
      requestAnimationFrame(this.animate)
    },
    validateNewFile() {
      if (this.newFile == null) { return false }
      this.validateFileErrors = [];
      const filename = this.newFile.name;
      let pos = filename.lastIndexOf('.')
      if (pos === -1) { 
        this.validateFileErrors.push('.stlファイルではありません。')
        return false
      }
      const ext = filename.slice(pos + 1).toLowerCase();
      if (this.allowedExtensions.indexOf(ext) === -1 ) {
        this.validateFileErrors.push('.stlファイルではありません。')
        return false
      }
      return true
    },
    startQuote() {
      this.loadNewFile();
    },
    loadNewFile() {
      this.resetView()

      this.isLoading = true

      const reader = new FileReader()
      const file = this.newFile
      const stlLoader = new STLLoader()
      this.viewingFileName = file.name

      reader.addEventListener('load', (e) => {
        try {
          const rawGeo = stlLoader.parse(e.target.result)
          const adjustedGeo = this.adjustModelGeometry(rawGeo)
          this.updateModel(adjustedGeo)
          this.viewingFileVolume = this.getVolume(adjustedGeo)
          this.bBox = this.getBoundingBox(adjustedGeo)

          this.$store.commit('addQuote', {
            fileName: this.viewingFileName,
            width: this.bBoxWidth,
            height: this.bBoxHeight,
            depth: this.bBoxDepth,
            volume: this.viewingFileVolume,
          });
        } catch (e) {
          console.error(e)
          this.showLoadError()
        }
        this.isLoading = false
      })
      reader.readAsArrayBuffer(file)
    },
    showLoadError() {
      this.status = 'error'
      this.resetView()
    },
    resetView() {
      this.scene.remove(this.modelMesh)
      this.modelGeo.dispose()
      this.viewingFileName = ''
    },
    adjustModelGeometry(geo) {
      geo.rotateX(-Math.PI / 2);
      geo.center();
      const lowest = this.getLowestPosition(geo)
      geo.translate(0, -lowest, 0)
      return geo;
    },
    updateModel(geo) {
      this.modelGeo = geo
      const mtrl = new THREE.MeshPhongMaterial({
        color: 0xff9900
      });
      this.modelMesh = new THREE.Mesh(this.modelGeo, mtrl)
      
      this.scene.add(this.modelMesh)

      this.status = 'view'
    },
    getVolume(geo) {
      let position = geo.attributes.position;
      let faces = position.count / 3;
      let sum = 0;

      let p1 = new THREE.Vector3();
      let p2 = new THREE.Vector3();
      let p3 = new THREE.Vector3();

      for (let i = 0; i < faces; i++) {
        p1.fromBufferAttribute(position, i * 3);
        p2.fromBufferAttribute(position, i * 3 + 1);
        p3.fromBufferAttribute(position, i * 3 + 2);
        sum += this.signedVolumeOfTriangle(p1, p2, p3);
      }

      sum = Math.floor(sum / 10) / 100;
      return sum;
    },
    getLowestPosition(geo) {
      let lowest = 0;
      let vertices = new THREE.Geometry().fromBufferGeometry(geo).vertices;
      for (let i = 0; i < vertices.length; i++) {
        let y = vertices[i].y
        if (y < lowest) {
          lowest = y
        }
      }
      return lowest
    },
    signedVolumeOfTriangle(p1, p2, p3) {
      return p1.dot(p2.cross(p3)) / 6.0;
    },
    getBoundingBox(geo) {
      geo.computeBoundingBox();
      const box = geo.boundingBox.clone();

      return box;
    },
    ...mapMutations([
      'addQuote'
    ])
  }
}
</script>

<style lang="scss" scoped>
.canvas-wrap {
  position: relative;
  width: 100%;
  height: 400px;
  border-radius: 15px;
  overflow: hidden;
}

canvas {
  width: 100%;
  height: 400px;
  cursor: all-scroll;
}

.message {
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 450px;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 15px rgba(0,0,0,.5);
}

.viewing-file-name {
  position: absolute;
  top: 20px;
  left: 20px;
}

.new-file-button {
  position: absolute;
  bottom: 20px;
  left: 20px;
}
</style>